import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type SyntheticEvent } from 'react';

import { Button } from '../../components/Button';
import { Field, inputClass } from '../../components/Field';
import { FormProblem } from '../../components/FormProblem';
import { useToast } from '../../components/Toast';
import { useFocusTrap } from '../../lib/useFocusTrap';
import { useAuth } from '../auth/AuthProvider';
import {
  noProblem,
  setTenantSlug,
  tenantsKey,
  toProblem,
  type Problem,
  type TenantSummary,
} from './api';

interface Props {
  tenant: TenantSummary;
  onClose: () => void;
}

/**
 * Changes the identifier typed at login (ADR-028). Data and sessions refer to
 * the tenant by id, so only the login changes.
 */
export function SlugDialog({ tenant, onClose }: Props) {
  const { request, session, setTenant } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [slug, setSlug] = useState(tenant.slug);
  const [problem, setProblem] = useState<Problem>(noProblem);
  useFocusTrap(ref, onClose);

  const save = useMutation({
    mutationFn: () => setTenantSlug(request, tenant.id, slug),
    onSuccess: async (t) => {
      await qc.invalidateQueries({ queryKey: tenantsKey });
      // Renaming one's own tenant (the platform) updates the session too.
      if (session && session.tenant.id === t.id) setTenant({ ...session.tenant, slug: t.slug });
      notify(`Identificador de ${t.name} alterado para "${t.slug}". Use-o no próximo login.`);
      onClose();
    },
    onError: (err) => {
      setProblem(toProblem(err));
    },
  });

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    setProblem(noProblem);
    save.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4">
      <div aria-hidden="true" className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-sm rounded-lg border border-line-strong bg-surface-raised p-5 shadow-2xl shadow-black/50"
      >
        <h2 id={titleId} className="text-sm font-semibold text-fg">
          Alterar identificador de {tenant.name}
        </h2>
        <p className="mt-1 text-[13px] leading-relaxed text-fg-muted">
          É o que as pessoas da empresa digitam no campo &quot;Organização&quot; do login. Quem já
          está conectado continua conectado; câmeras, gravações e acessos não mudam. Avise as
          pessoas antes.
        </p>
        <form onSubmit={submit} noValidate className="mt-4 space-y-4">
          <FormProblem message={problem.form} />
          <Field label="Novo identificador" error={problem.fields.slug}>
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                data-autofocus
                value={slug}
                autoCapitalize="off"
                spellCheck={false}
                onChange={(e) => {
                  setSlug(e.target.value);
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={`${inputClass} font-mono`}
              />
            )}
          </Field>
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              variant="primary"
              type="submit"
              disabled={save.isPending || slug.trim() === tenant.slug}
            >
              {save.isPending ? 'Salvando…' : 'Alterar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
